import React, { createContext, useContext, useRef, useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const SocketContext = createContext();

// Singleton pattern - keep socket connection outside React lifecycle
let globalSocket = null;
let connectionPromise = null;

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const { admin, user, loading } = useAuth();
  const mountedRef = useRef(true);
  const prevAuthRef = useRef(null);

  // Determine current logged-in entity
  const authData = admin || user;

  const createConnection = useCallback(async () => {
    if (!authData || loading) return null;

    // Return existing connection if it's still valid
    if (globalSocket) {
      return globalSocket;
    }

    // Return existing connection promise if already connecting
    if (connectionPromise) {
      return connectionPromise;
    }

    console.log("Creating new socket connection...");

    // connectionPromise = new Promise((resolve, reject) => {
    //   const newSocket = io("https://api.marketlite.app", {
    //     query: {
    //       id: authData.id,
    //       role: authData.role, 
    //     },
    //     autoConnect: true,
    //     reconnection: true,
    //     reconnectionDelay: 1000,
    //     reconnectionAttempts: 5,
    //     timeout: 20000,
    //     forceNew: true, // Force new connection
    //   });

      connectionPromise = new Promise((resolve, reject) => {
       const newSocket = io(process.env.REACT_APP_API_URL.replace('/api/v1', ''), {
         query: {
           id: authData.id,
           role: authData.role,
         },
         autoConnect: true,
         reconnection: true,
         reconnectionDelay: 1000,
         reconnectionAttempts: 5,
         timeout: 20000,
         forceNew: true, // Force new connection
       });

      const connectTimeout = setTimeout(() => {
        reject(new Error("Connection timeout"));
      }, 10000);

      newSocket.on("connect", () => {
        clearTimeout(connectTimeout);
        console.log("WebSocket connected:", newSocket.id);
        globalSocket = newSocket;
        connectionPromise = null;
        resolve(newSocket);
      });

      newSocket.on("connect_error", (err) => {
        clearTimeout(connectTimeout);
        console.error("WebSocket connection error:", err);
        connectionPromise = null;
        reject(err);
      });

      newSocket.on("disconnect", (reason) => {
        console.log("WebSocket disconnected:", reason);
        // Don't clear globalSocket here - let reconnection handle it
      });
    });

    try {
      return await connectionPromise;
    } catch (error) {
      connectionPromise = null;
      throw error;
    }
  }, [authData?.id, authData?.role, loading]);

  useEffect(() => {
    mountedRef.current = true;

    if (loading || !authData) {
      setSocket(null);
      setIsConnected(false);
      // Disconnect global socket when no auth
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
      }
      connectionPromise = null;
      prevAuthRef.current = null;
      return;
    }

    // Auth change handling
    const currentAuth = {id: authData.id, role: authData.role};
    if (prevAuthRef.current && (prevAuthRef.current.id !== currentAuth.id || prevAuthRef.current.role !== currentAuth.role)) {
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
      }
      connectionPromise = null;
    }
    prevAuthRef.current = currentAuth;

    // Use existing connection or create new one
    const initializeSocket = async () => {
      try {
        let socketInstance = globalSocket;
        
        if (!socketInstance || !socketInstance.connected) {
          socketInstance = await createConnection();
        }

        if (mountedRef.current && socketInstance) {
          setSocket(socketInstance);
          setIsConnected(socketInstance.connected);

          // Set up event listeners for this component instance
          const handleConnect = () => {
            if (mountedRef.current) {
              setIsConnected(true);
            }
          };

          const handleDisconnect = () => {
            if (mountedRef.current) {
              setIsConnected(false);
            }
          };

          socketInstance.on("connect", handleConnect);
          socketInstance.on("disconnect", handleDisconnect);

          // Cleanup listeners when effect reruns or component unmounts
          return () => {
            socketInstance.off("connect", handleConnect);
            socketInstance.off("disconnect", handleDisconnect);
          };
        }
      } catch (error) {
        console.error("Failed to initialize socket:", error);
        if (mountedRef.current) {
          setSocket(null);
          setIsConnected(false);
        }
      }
    };

    const cleanup = initializeSocket();
    
    return () => {
      if (cleanup instanceof Promise) {
        cleanup.then(cleanupFn => cleanupFn?.());
      } else if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [authData?.id, authData?.role, loading, createConnection]);

  // Mark component as unmounted
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const contextValue = {
    socket,
    isConnected,
    disconnect: useCallback(() => {
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
        connectionPromise = null;
        setSocket(null);
        setIsConnected(false);
      }
    }, [])
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context.socket;
};

export const useSocketConnection = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocketConnection must be used within a SocketProvider");
  }
  return context;
};

// Optional helper for manual disconnect
export const useSocketDisconnect = () => {
  const { disconnect } = useSocketConnection();
  return disconnect;
};

// Hook to check connection status
export const useSocketStatus = () => {
  const { isConnected } = useSocketConnection();
  return isConnected;
};